import React from "react"
import {
	Accordion, AccordionDetails, AccordionSummary, Box, Typography,
	Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { AppButtonIcon, AppCheckCrossIcon, AppValueChip, NoDataFound } from "@components"
import { dsColorNew } from "@palette"

// Group an array into a Map keyed by keyFn(item), preserving insertion order.
const groupBy = (arr, keyFn) => {
	const map = new Map()
	for (const item of arr) {
		const key = keyFn(item)
		if (!map.has(key)) map.set(key, [])
		map.get(key).push(item)
	}
	return map
}

const centered = v => <Box sx={{ display: 'flex', justifyContent: 'center' }}>{v ?? ''}</Box>

// Report card: icon tile, name and its executions count; the rows open below it
function GroupNode({ icon, title, count, countLabel, children }) {
	return (
		<Accordion
			disableGutters
			elevation={0}
			TransitionProps={{ unmountOnExit: true }}
			sx={{
				backgroundColor: dsColorNew.white,
				border: `1px solid ${dsColorNew.grayBorder}`,
				borderRadius: '12px !important', '&:before': { display: 'none' }, mb: 1,
				overflow: 'hidden',
			}}
		>
			<AccordionSummary
				expandIcon={<IconSeeker icon="chevron-down" sx={{ fontSize: 13, color: dsColorNew.textMuted }}/>}
				sx={{ px: 2, '&:hover': { backgroundColor: dsColorNew.hoverBgLight } }}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', pr: 1 }}>
					<Box sx={{
						width: 32, height: 32, borderRadius: '8px', flexShrink: 0,
						display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
						backgroundColor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue,
					}}>
						<IconSeeker icon={icon} sx={{ fontSize: 13 }}/>
					</Box>
					<Typography sx={{ fontSize: 14, fontWeight: 600, color: dsColorNew.textTitle }}>{title}</Typography>
					<Box sx={{ flexGrow: 1 }}/>
					<AppValueChip>{`${count} ${countLabel}`}</AppValueChip>
				</Box>
			</AccordionSummary>
			<AccordionDetails sx={{ p: 0, borderTop: `1px solid ${dsColorNew.grayBorder}` }}>{children}</AccordionDetails>
		</Accordion>
	)
}

// One table per report: execution and pricing tool written once for the rows they hold (row span), then each office
function ReportTable({ rows, onOpenNotifications }) {
	const lines = []
	for (const execRows of groupBy(rows, r => r.idReportExecution).values()) {
		let first = true
		for (const toolRows of groupBy(execRows, r => r.toolId).values()) {
			toolRows.forEach((row, i) => {
				lines.push({ row, execSpan: first ? execRows.length : 0, toolSpan: i === 0 ? toolRows.length : 0 })
				first = false
			})
		}
	}

	const spanSx = { verticalAlign: 'top', backgroundColor: dsColorNew.white, borderRight: `1px solid ${dsColorNew.grayBorder}` }

	return (
		<Table
			size="small"
			sx={{
				backgroundColor: dsColorNew.white,
				'& .MuiTableCell-root': { borderColor: dsColorNew.grayBorder },
				'& thead .MuiTableCell-root': { borderRadius: 0 },
				'& tbody .MuiTableCell-root': { color: dsColorNew.textDark, fontSize: 13 },
				'& tbody tr:nth-of-type(even) .office-cell': { backgroundColor: dsColorNew.tableRowAlt },
			}}
		>
			<TableHead>
				<TableRow>
					<TableCell>Execution</TableCell>
					<TableCell>Pricing Tool</TableCell>
					<TableCell>Office</TableCell>
					<TableCell align="center">Checked</TableCell>
					<TableCell align="center">Num Notifications</TableCell>
					<TableCell align="center">Exported</TableCell>
					<TableCell align="center">Warning</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{lines.map(({ row, execSpan, toolSpan }, i) => (
					<TableRow key={i}>
						{!!execSpan && <TableCell rowSpan={execSpan} sx={{ ...spanSx, fontWeight: 600 }}>{row.executionTime ?? ''}</TableCell>}
						{!!toolSpan && <TableCell rowSpan={toolSpan} sx={spanSx}>{row.toolName ?? ''}</TableCell>}
						<TableCell className="office-cell">{row.idOfficePickupName ?? ''}</TableCell>
						<TableCell className="office-cell" align="center">{centered(row.checked
							? <AppButtonIcon
								icon="check"
								tooltip="View notifications"
								sx={{ color: dsColorNew.successText }}
								onClick={() => onOpenNotifications(row)}
							/>
							: <AppCheckCrossIcon condition={false}/>)}</TableCell>
						<TableCell className="office-cell" align="center">{row.numNotifications ?? 0}</TableCell>
						<TableCell className="office-cell" align="center">{centered(row.exported && row.exportUrl
							? <AppButtonIcon
								icon="file-arrow-down"
								tooltip="Download export"
								sx={{ color: dsColorNew.appBlue }}
								onClick={() => window.open(row.exportUrl, '_blank')}
							/>
							: <AppCheckCrossIcon condition={!!row.exported}/>)}</TableCell>
							<TableCell align="center">{centered(row.warning
								? <IconSeeker
									icon="triangle-exclamation"
									tooltip={(row.warningReasons ?? []).join(', ')}
									sx={{ color: 'warning.main' }}
								/>
								: '')}</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}

export default function GroupedCheckers({ data, onOpenNotifications }) {
	const byReport = groupBy(data, r => r.idReport)

	if (!data.length) return <NoDataFound title="No data available." text=""/>

	return (
		<Box>
			{[...byReport.entries()].map(([idReport, reportRows]) => (
				<GroupNode
					key={idReport}
					icon="file-lines"
					title={reportRows[0]?.reportName || 'Report'}
					count={groupBy(reportRows, r => r.idReportExecution).size}
					countLabel="executions"
				>
					<ReportTable rows={reportRows} onOpenNotifications={onOpenNotifications}/>
				</GroupNode>
			))}
		</Box>
	)
}
